/**
 * DataManagerWeapon.ts
 *
 * Runtime manager for weapon data.
 * Merges:
 * - weapons-c3data.json (base data from C3 extraction)
 * - weapons-overrides.json (gameplay overrides)
 * - weapons-defaults.json (initial player state)
 *
 * Handles all weapon categories: guns, bombs, and tractors.
 * This follows the same c3data + overrides pattern as entities and projectiles.
 *
 * Documentation: See docs/weapons_system.md for full system overview
 */

import Phaser from 'phaser';
import { Allegiance } from '../constants/enums';

// Re-export for consumers that imported from here
export { Allegiance };

// ============================================================================
// Weapon Types
// ============================================================================

export type SlotType = 'gun' | 'tractor' | 'bomb';
export type WeaponState = 'active' | 'tobuy' | 'inactive';

export interface FirePoint {
  x: number;
  y: number;
}

export interface BurstConfig {
  count: number;
  delay: number;
  initialScales?: Array<{ x: number; y: number }>;
}

// Shop configuration types
export interface SlotConfig {
  key: string;
  name: string;
  baseWeapon: string;
}

export interface ShopConfig {
  weaponSwapCost: number;
  maxWeaponsPerSlot: number;
  slots: Record<SlotType, SlotConfig>;
}

// Defaults file types
export interface SlotLoadout {
  equipped: string[];
  activeIndex: number;
}

export interface WeaponDefaultsData {
  discovered: string[];
  initialAmmo: Record<string, number>;
  initialLoadout: Record<SlotType, SlotLoadout>;
}

export interface WeaponDefaultsFile {
  _meta?: { description: string; note: string };
  discovered: string[];
  initialAmmo: Record<string, number>;
  initialLoadout: Record<SlotType, SlotLoadout>;
  testingMode?: WeaponDefaultsData;
}

export interface WeaponDefinition {
  id: string;
  name: string;
  slot: SlotType;
  isBase: boolean;
  hasInfiniteAmmo: boolean;
  projectileType?: string;
  fireRate: number;
  damage: number;
  projectileSpeed: number;
  firePoints: FirePoint[];
  imagePoints?: string[];
  fireAngle: number;
  sound?: string;
  behavior?: 'normal' | 'hold' | 'homing' | 'gravity';
  description?: string;
  descriptionKey?: string;
  burst?: BurstConfig;
  // Shop-related fields
  packSize?: number;
  packCost?: number;
  initialAmmo?: number;
}

export interface TractorBeamAnimation {
  name: string;
  frameCount: number;
  speed: number;
  isLooping: boolean;
}

export interface TractorParticlesConfig {
  lineSpawnInterval: number;
  lineRiseSpeed: number;
  lineLifespan: number;
  burnMarkInterval: number;
  maxBurnMarks: number;
}

export interface TractorDefinition extends WeaponDefinition {
  // Visual properties from C3
  width: number;
  height: number;
  originX: number;
  originY: number;
  animations: TractorBeamAnimation[];
  effects: string[];
  behaviors: string[];
  // Gameplay properties from overrides
  texture: string;
  offsetY: number;
  scale: number;
  pulseSpeed: number;
  wobbleAmount: number;
  fadeInDuration: number;
  fadeOutDuration: number;
  attractionSpeed: number;
  attractionRange: number;
  tractorPower: number;
  /** Particle/burn mark tint color (hex number, e.g. 0x00ffff for cyan) */
  particleTint: number;
  /** Particle effect configuration for rising lines and burn marks */
  particles?: TractorParticlesConfig;
  // Visual/logic flags (overrides)
  /** Si es true, el rayo no se corta con el suelo */
  ignoreSolids?: boolean;
  /** Si es true, el rayo ignora bloqueadores */
  ignoreBlockers?: boolean;
  /** Depth visual del rayo (numero Phaser depth) */
  visualDepth?: number;
}

export interface WeaponInstance {
  definition: WeaponDefinition;
  state: WeaponState;
  ammo: number;
  lastFiredAt: number;
}

// ============================================================================
// C3 Data Types
// ============================================================================

interface WeaponSpawnC3Data {
  weaponClass: string;
  projectileType: string;
  fireAngle: number | string;
  spawnPoint: string;
  setAngleAction?: string;
}

interface TractorC3Data {
  id: string;
  width: number;
  height: number;
  originX: number;
  originY: number;
  animations: TractorBeamAnimation[];
  effects: string[];
  behaviors: string[];
}

interface WeaponsC3DataFile {
  _meta: {
    extractedAt: string;
    sources: string[];
    warning: string;
  };
  guns: WeaponSpawnC3Data[];
  gunsSummary: Record<string, { projectile: string; angle: number | string }>;
  tractors: Record<string, TractorC3Data>;
}

interface WeaponsOverridesFile {
  _meta?: {
    description: string;
    note: string;
  };
  shopConfig?: ShopConfig;
  guns: Record<string, Partial<WeaponDefinition>>;
  bombs: Record<string, Partial<WeaponDefinition>>;
  tractors: Record<string, Partial<TractorDefinition>>;
  defaultPickupAmmo: Record<string, number>;
}

// ============================================================================
// Slot Configuration
// ============================================================================

export const SLOT_KEYS: Record<SlotType, string> = {
  gun: 'X',
  tractor: 'Z',
  bomb: 'C',
};

export const SLOT_KEYCODES: Record<SlotType, number> = {
  gun: Phaser.Input.Keyboard.KeyCodes.X,
  tractor: Phaser.Input.Keyboard.KeyCodes.Z,
  bomb: Phaser.Input.Keyboard.KeyCodes.C,
};

// ============================================================================
// DataManagerWeapon
// ============================================================================

/**
 * Static manager for weapon data.
 * Merges C3 extracted data with gameplay overrides at runtime.
 */
export class DataManagerWeapon {
  // Merged definitions by category
  private static guns: Map<string, WeaponDefinition> = new Map();
  private static bombs: Map<string, WeaponDefinition> = new Map();
  private static tractors: Map<string, TractorDefinition> = new Map();
  private static defaultPickupAmmo: Map<string, number> = new Map();

  // All weapons indexed by ID
  private static allWeapons: Map<string, WeaponDefinition> = new Map();

  // Shop config and defaults
  private static shopConfig: ShopConfig | null = null;
  private static defaults: WeaponDefaultsFile | null = null;

  private static loaded: boolean = false;

  static readonly C3_JSON_KEY = 'weapons-c3data';
  static readonly OVERRIDES_JSON_KEY = 'weapons-overrides';
  static readonly DEFAULTS_JSON_KEY = 'weapons-defaults';

  /**
   * Preload JSON files (call in scene preload)
   */
  static preload(scene: Phaser.Scene): void {
    if (!scene.cache.json.exists(this.C3_JSON_KEY)) {
      scene.load.json(this.C3_JSON_KEY, '/assets/data/weapons-c3data.json');
    }
    if (!scene.cache.json.exists(this.OVERRIDES_JSON_KEY)) {
      scene.load.json(this.OVERRIDES_JSON_KEY, '/assets/data/weapons-overrides.json');
    }
    if (!scene.cache.json.exists(this.DEFAULTS_JSON_KEY)) {
      scene.load.json(this.DEFAULTS_JSON_KEY, '/assets/data/weapons-defaults.json');
    }
  }

  /**
   * Load and merge data from cache (call in scene create)
   */
  static load(scene: Phaser.Scene): void {
    // Skip if already loaded AND data exists (protects against partial loads)
    if (this.loaded && this.tractors.size > 0) {
      return;
    }

    // Reset if data was lost but flag was still true
    if (this.loaded && this.tractors.size === 0) {
      console.warn('[DataManagerWeapon] Data lost, reloading...');
      this.loaded = false;
    }

    // Load C3 data
    const c3Json = scene.cache.json.get(this.C3_JSON_KEY) as WeaponsC3DataFile | undefined;
    if (!c3Json) {
      console.warn('[DataManagerWeapon] weapons-c3data.json not loaded');
    }

    // Load overrides
    const overridesJson = scene.cache.json.get(this.OVERRIDES_JSON_KEY) as WeaponsOverridesFile | undefined;
    if (!overridesJson) {
      console.warn('[DataManagerWeapon] weapons-overrides.json not loaded');
    }

    // Build guns
    if (overridesJson?.guns) {
      for (const [id, override] of Object.entries(overridesJson.guns)) {
        const merged = this.buildWeaponDefinition(id, override);
        this.guns.set(id, merged);
        this.allWeapons.set(id, merged);
      }
    }

    // Build bombs
    if (overridesJson?.bombs) {
      for (const [id, override] of Object.entries(overridesJson.bombs)) {
        const merged = this.buildWeaponDefinition(id, override);
        this.bombs.set(id, merged);
        this.allWeapons.set(id, merged);
      }
    }

    // Build tractors (merge C3 visual data + overrides)
    if (overridesJson?.tractors) {
      for (const [id, override] of Object.entries(overridesJson.tractors)) {
        const c3Tractor = c3Json?.tractors?.[id];
        const merged = this.buildTractorDefinition(id, c3Tractor, override);
        this.tractors.set(id, merged);
        this.allWeapons.set(id, merged);
      }
    }

    // Load default pickup ammo
    if (overridesJson?.defaultPickupAmmo) {
      for (const [id, amount] of Object.entries(overridesJson.defaultPickupAmmo)) {
        this.defaultPickupAmmo.set(id, amount);
      }
    }

    // Load shop config
    if (overridesJson?.shopConfig) {
      this.shopConfig = overridesJson.shopConfig;
    }

    // Load defaults
    const defaultsJson = scene.cache.json.get(this.DEFAULTS_JSON_KEY) as WeaponDefaultsFile | undefined;
    if (defaultsJson) {
      this.defaults = defaultsJson;
    } else {
      console.warn('[DataManagerWeapon] weapons-defaults.json not loaded');
    }

    this.loaded = true;
    // console.log(
    //   `[DataManagerWeapon] Loaded ${this.guns.size} guns, ${this.bombs.size} bombs, ${this.tractors.size} tractors`
    // );
  }

  /**
   * Build a weapon definition with defaults
   */
  private static buildWeaponDefinition(
    id: string,
    override: Partial<WeaponDefinition>
  ): WeaponDefinition {
    return {
      id,
      name: id,
      slot: 'gun',
      isBase: false,
      hasInfiniteAmmo: false,
      fireRate: 200,
      damage: 1,
      projectileSpeed: 300,
      firePoints: [{ x: 0, y: 30 }],
      fireAngle: 90,
      behavior: 'normal',
      ...override,
    };
  }

  /**
   * Build a tractor definition merging C3 data + overrides
   */
  private static buildTractorDefinition(
    id: string,
    c3Data: TractorC3Data | undefined,
    override: Partial<TractorDefinition>
  ): TractorDefinition {
    return {
      // Defaults
      id,
      name: id,
      slot: 'tractor',
      isBase: false,
      hasInfiniteAmmo: false,
      fireRate: 0,
      damage: 0,
      projectileSpeed: 0,
      firePoints: [{ x: 0, y: 40 }],
      fireAngle: 90,
      behavior: 'hold',

      // C3 visual data
      width: c3Data?.width ?? 115,
      height: c3Data?.height ?? 311,
      originX: c3Data?.originX ?? 0.5,
      originY: c3Data?.originY ?? 0.01,
      animations: c3Data?.animations ?? [],
      effects: c3Data?.effects ?? [],
      behaviors: c3Data?.behaviors ?? [],

      // Tractor-specific defaults
      texture: `${id}_start_0`,
      offsetY: -20,
      scale: 1.0,
      pulseSpeed: 0.005,
      wobbleAmount: 2,
      fadeInDuration: 150,
      fadeOutDuration: 100,
      attractionSpeed: 200,
      attractionRange: 150,
      tractorPower: 100,
      particleTint: 0x00ffff, // Default cyan

      // Override values (last = highest priority)
      ...override,
    };
  }

  // ============================================================================
  // Getters
  // ============================================================================

  /**
   * Get weapon by ID (any type)
   */
  static get(id: string): WeaponDefinition | undefined {
    return this.allWeapons.get(id);
  }

  /**
   * Get gun by ID
   */
  static getGun(id: string): WeaponDefinition | undefined {
    return this.guns.get(id);
  }

  /**
   * Get bomb by ID
   */
  static getBomb(id: string): WeaponDefinition | undefined {
    return this.bombs.get(id);
  }

  /**
   * Get tractor by ID
   */
  static getTractor(id: string): TractorDefinition | undefined {
    return this.tractors.get(id);
  }

  /**
   * Get tractor beam definition by weapon ID
   * Maps weapon IDs (punytractor) to tractor IDs (punytractorbeam)
   */
  static getTractorBeamDefinition(weaponId: string): TractorDefinition | undefined {
    const mapping: Record<string, string> = {
      punytractor: 'punytractorbeam',
      megatractor: 'megatractorbeam',
    };
    const tractorId = mapping[weaponId.toLowerCase()] || weaponId;
    return this.tractors.get(tractorId);
  }

  /**
   * Get all weapons for a specific slot
   */
  static getWeaponsForSlot(slot: SlotType): WeaponDefinition[] {
    switch (slot) {
      case 'gun':
        return Array.from(this.guns.values());
      case 'bomb':
        return Array.from(this.bombs.values());
      case 'tractor':
        return Array.from(this.tractors.values());
    }
  }

  /**
   * Get the base weapon for a slot
   */
  static getBaseWeaponForSlot(slot: SlotType): WeaponDefinition | undefined {
    return this.getWeaponsForSlot(slot).find((w) => w.isBase);
  }

  /**
   * Get default pickup ammo amount
   */
  static getDefaultPickupAmmo(weaponId: string): number {
    return this.defaultPickupAmmo.get(weaponId) ?? 0;
  }

  /**
   * Create a new weapon instance
   */
  static createWeaponInstance(
    weaponId: string,
    initialAmmo?: number
  ): WeaponInstance | undefined {
    const definition = this.get(weaponId);
    if (!definition) return undefined;

    return {
      definition,
      state: definition.isBase ? 'active' : 'tobuy',
      ammo: definition.hasInfiniteAmmo
        ? -1
        : initialAmmo ?? this.getDefaultPickupAmmo(weaponId),
      lastFiredAt: 0,
    };
  }

  /**
   * Check if definition exists
   */
  static has(id: string): boolean {
    return this.allWeapons.has(id);
  }

  /**
   * Get all weapon IDs
   */
  static getIds(): string[] {
    return Array.from(this.allWeapons.keys());
  }

  /**
   * Get all gun IDs
   */
  static getGunIds(): string[] {
    return Array.from(this.guns.keys());
  }

  /**
   * Get all bomb IDs
   */
  static getBombIds(): string[] {
    return Array.from(this.bombs.keys());
  }

  /**
   * Get all tractor IDs
   */
  static getTractorIds(): string[] {
    return Array.from(this.tractors.keys());
  }

  // ============================================================================
  // Shop Config Getters
  // ============================================================================

  /**
   * Get full shop configuration
   */
  static getShopConfig(): ShopConfig | null {
    return this.shopConfig;
  }

  /**
   * Get weapon swap cost
   */
  static getSwapCost(): number {
    return this.shopConfig?.weaponSwapCost ?? 100;
  }

  /**
   * Get max weapons per slot
   */
  static getMaxWeaponsPerSlot(): number {
    return this.shopConfig?.maxWeaponsPerSlot ?? 4;
  }

  /**
   * Get slot configuration
   */
  static getSlotConfig(slot: SlotType): SlotConfig | undefined {
    return this.shopConfig?.slots[slot];
  }

  // ============================================================================
  // Defaults Getters
  // ============================================================================

  /**
   * Get full defaults file
   */
  static getDefaults(): WeaponDefaultsFile | null {
    return this.defaults;
  }

  /**
   * Get default weapon state for Storage (production mode)
   * Returns the state structure expected by Storage.WeaponState
   */
  static getDefaultWeaponState(testingMode: boolean = false): {
    discovered: string[];
    ammo: Record<string, number>;
    loadout: Record<SlotType, SlotLoadout>;
  } | null {
    if (!this.defaults) {
      console.error('[DataManagerWeapon] Defaults not loaded');
      return null;
    }

    const source = testingMode && this.defaults.testingMode
      ? this.defaults.testingMode
      : this.defaults;

    return {
      discovered: [...source.discovered],
      ammo: { ...source.initialAmmo },
      loadout: {
        gun: { ...source.initialLoadout.gun },
        tractor: { ...source.initialLoadout.tractor },
        bomb: { ...source.initialLoadout.bomb },
      },
    };
  }

  /**
   * Check if data has been loaded
   */
  static isLoaded(): boolean {
    return this.loaded;
  }

  /**
   * Reset manager (for testing or scene restarts)
   */
  static reset(): void {
    this.guns.clear();
    this.bombs.clear();
    this.tractors.clear();
    this.defaultPickupAmmo.clear();
    this.allWeapons.clear();
    this.shopConfig = null;
    this.defaults = null;
    this.loaded = false;
  }
}
